import { findSupplyStructureRoot } from "src/util/supplyStructureInstance";

export type DirectFlagBag = {
  enableOrgDesign?: boolean | null | undefined;
  /** Accepted so callers can pass the workspace bag. Not part of these gates. */
  enableDemoODOptions?: boolean | null | undefined;
};

export type TeamInsightsFlagBag = DirectFlagBag & {
  enableTeamInsights?: boolean | null | undefined;
};

export type TeamOwnerFlagBag = DirectFlagBag & {
  enableTeamOwner?: boolean | null | undefined;
};

export type TeamOwnerEditSubject = {
  id?: string | null;
  isRootForSource?: boolean | null;
  enableSupplyStructureEdits?: boolean | null;
  hierarchyIds?: string[] | null;
  supplyStructureRoot?: {
    id?: string | null;
    name?: string | null;
    isRootForSource?: boolean | null;
    enableSupplyStructureEdits?: boolean | null;
  } | null;
};

type OrgDesignDirectFlagName =
  | "enableChangelog"
  | "enableTeamInsights"
  | "enableTeamOwner";

/**
 * Org design release on ⇒ the direct flag is on. Not written back: org design
 * off leaves the stored flag (default off unless turned on manually). Beta
 * does not affect any of these flags.
 */
export const withOrgDesignAutoEnable = <
  F extends OrgDesignDirectFlagName,
  T extends DirectFlagBag & { [K in F]?: boolean | null | undefined },
>(
  flags: T,
  flag: F
): T => {
  if (!flags?.enableOrgDesign || flags[flag]) {
    return flags;
  }
  return { ...flags, [flag]: true };
};

export const withTeamInsightsAutoEnable = <T extends TeamInsightsFlagBag>(
  flags: T
): T => withOrgDesignAutoEnable(flags, "enableTeamInsights");

export const withTeamOwnerAutoEnable = <T extends TeamOwnerFlagBag>(
  flags: T
): T => withOrgDesignAutoEnable(flags, "enableTeamOwner");

export const isTeamInsightsEnabled = (
  flags: TeamInsightsFlagBag | null | undefined
): boolean =>
  Boolean(withTeamInsightsAutoEnable(flags ?? {}).enableTeamInsights);

export const isTeamOwnerEnabled = (
  flags: TeamOwnerFlagBag | null | undefined
): boolean => Boolean(withTeamOwnerAutoEnable(flags ?? {}).enableTeamOwner);

/**
 * Editable team owner. Needs the effective team-owner flag. With org design on,
 * that is enough: the supply-root edit setting is ignored. Otherwise (stored
 * team-owner flag only) the supply-structure root's enableSupplyStructureEdits
 * must be on, and a missing root config is read-only. enableDemoODOptions is
 * not an input.
 */
export const isTeamOwnerEditable = (
  flags: TeamOwnerFlagBag | null | undefined,
  team: TeamOwnerEditSubject | null | undefined,
  teamsById?: Record<string, TeamOwnerEditSubject> | null
): boolean => {
  if (!isTeamOwnerEnabled(flags)) {
    return false;
  }
  if (flags?.enableOrgDesign) {
    return true;
  }
  if (!team?.id) {
    return false;
  }
  const map = {
    ...(teamsById ?? {}),
    [team.id]: { ...(teamsById?.[team.id] ?? {}), ...team },
  };
  return Boolean(findSupplyStructureRoot(team.id, map)?.enableEdits);
};
